import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ClearIcon from '@material-ui/icons/Clear';
import cx from 'classnames';
import CircularLoading from 'components/base/loading/CircularLoading';
import T from 'components/common/text/T';
import { validInputString } from 'lib/library';
import { useDidUpdateEffect } from 'lib/utils';
import { debounce, throttle } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import { color } from 'styles/utils';
import { IPartner } from 'lib/types/projectTypes';
import { useShallowAppSelector } from 'store/hooks';
import { PartnerActions } from 'store/actionCreators';
import CustomSpan from '../text/CustomSpan';
import BasicTooltip from '../tooltip/BasicTooltip';

interface IPatientSelect extends IPartner {
  partnerState: string;
}

interface AsyncFetchSelectSyncPartnerProps {
  className?: string;
  disabled?: boolean;
  isOpenUp?: boolean;
  placeholder?: string;
  idKey: string;
  labelKey: string;
  exceptId: number | null;
  //
  partnerGroupIdx: number | '';
  syncInfo: any | null;
  projectPartner: any | null;
  selectedValue: number | '';
  setSelectedValue: React.Dispatch<number | ''>;
  selectedName: string;
  setSelectedName: React.Dispatch<string>;
  selectedSyncState: string;
  setSelectedSyncState: React.Dispatch<string>;
}

const AsyncFetchSelectSyncPartner = ({
  className = '',
  disabled = false,
  isOpenUp = false,
  placeholder = '',
  idKey = '',
  labelKey = '',
  exceptId = null,
  // NEW
  partnerGroupIdx = '',
  syncInfo,
  projectPartner,
  selectedValue = '',
  setSelectedValue = () => {},
  selectedName = '',
  setSelectedName = () => {},
  selectedSyncState = '',
  setSelectedSyncState = () => {},
}: AsyncFetchSelectSyncPartnerProps) => {
  const {
    fetchProjectSuccess,
    fetchPartner: { list: partnerList, pagingData: partnerPagingData },
    fetchPartnerSuccess,
    fetchPartnerPending,
    createProjectSyncSuccess,
  }: {
    fetchProjectSuccess: boolean;
    fetchPartner: { list: IPartner[]; pagingData: any };
    fetchPartnerSuccess: boolean;
    fetchPartnerPending: boolean;
    createProjectSyncSuccess: boolean;
  } = useShallowAppSelector(state => ({
    fetchProjectSuccess: state.project.project.success,

    fetchPartner: state.partner.syncPartners.data || {},
    fetchPartnerSuccess: state.partner.syncPartners.success,
    fetchPartnerPending: state.partner.syncPartners.pending,
    createProjectSyncSuccess: state.project.createProjectSync.success,
  }));
  const syncPartnersPagingData = partnerPagingData?.pagingData;
  const hasMoreSyncPartners = syncPartnersPagingData?.page < syncPartnersPagingData?.totalPage;
  const { t } = useTranslation();
  const componentRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string>('');
  const [prevName, setPrevName] = useState<string>('');
  const [value, setValue] = useState<number | ''>('');
  const [prevValue, setPrevValue] = useState<number | ''>('');
  const [syncState, setSyncState] = useState<string>('');
  const [prevSyncState, setPrevSyncState] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [convertPartnerList, setConvertPartnerList] = useState<IPartner[]>([]);

  useEffect(() => {
    setSyncState(selectedSyncState);
    setPrevSyncState(selectedSyncState);
  }, [selectedSyncState]);

  useEffect(() => {
    setName(selectedName);
    setPrevName(selectedName);
    handleSearchSyncPartners(selectedName);
    if (!!selectedName) {
    }
  }, [selectedName]);

  useEffect(() => {
    setValue(selectedValue);
    setPrevValue(selectedValue);
  }, [selectedValue]);

  useDidUpdateEffect(() => {
    if (!fetchProjectSuccess) return;
    setConvertPartnerList([]);
    PartnerActions.fetch_sync_partners_request({
      keyword: selectedName,
      page: 1,
    });
  }, [fetchProjectSuccess, selectedName]);

  useDidUpdateEffect(() => {
    if (!fetchPartnerSuccess) return;
    setConvertPartnerList(
      partnerList.map((draft: IPartner) => {
        if (draft.userGroupIdx === syncInfo?.userGroupIdx) {
          return { ...draft, isSentRequest: true };
        } else if (draft.userGroupIdx === projectPartner) {
          return { ...draft, isSyncPartner: true };
        } else {
          return { ...draft };
        }
      }),
    );
  }, [fetchPartnerSuccess, partnerList, syncInfo, projectPartner]);

  // if select, onChange
  const onChangeInput = (value: any) => {
    setName(value);
    setSyncState('');
    setValue('');
    handleSearchSyncPartners(value);
  };

  // input onChange
  const handleChangeInput = throttle(e => {
    const checkText = validInputString(e.target.value);
    onChangeInput(checkText);
  }, 500);

  const handleCancel = () => {
    inputRef.current?.focus();
    handleSearchSyncPartners('');
    handleToggleOpen();
    if (!!partnerGroupIdx) {
      setSyncState('');
      setName('');
      setValue('');
    } else {
      setSelectedValue('');
      setSelectedName('');
      setSelectedSyncState('');
    }
  };

  const handleToggleDrop = () => {
    setOpen(!open);
  };

  const handleToggleOpen = () => {
    setOpen(true);
  };

  const handleClickSyncState = () => {
    setOpen(!open);
    if (!open) inputRef.current?.focus();
  };

  const handleSelect = (patient: IPatientSelect) => {
    if (patient.userGroupIdx !== selectedValue) {
      console.log('HDS] 다른 파트너 클릭');
      handleSearchSyncPartners(patient.name);
      setSelectedValue(patient.userGroupIdx);
      setSelectedName(patient.name);
      setSelectedSyncState(patient.partnerState);
      setOpen(false);
    } else {
      console.log('HDS] 같은 파트너 클릭');
      handleSearchSyncPartners(prevName);
      setValue(prevValue);
      setName(prevName);
      setSyncState(prevSyncState);
      setOpen(false);
    }
  };

  const handleBlurClick = (e: any) => {
    if (componentRef.current && !componentRef.current.contains(e.target) && !!open) {
      handleSearchSyncPartners(prevName);
      setValue(prevValue);
      setName(prevName);
      setSyncState(prevSyncState);
      setOpen(false);
    }
  };

  const handleFetchSyncPartners = debounce((first?: boolean) => {
    // if (first) return PartnerActions.fetch_sync_partners_request({ keyword: '', page: 1 });
    const { page, totalPage } = syncPartnersPagingData;
    if (page >= totalPage) return;
    PartnerActions.fetch_sync_partners_request({
      keyword: name || '',
      page: page + 1,
    });
  }, 500);

  const handleSearchSyncPartners = debounce((value: string) => {
    setConvertPartnerList([]);
    PartnerActions.fetch_sync_partners_request({ keyword: value || '', page: 1 });
  }, 0);

  useEffect(() => {
    document.addEventListener('mousedown', handleBlurClick);
    return () => {
      document.removeEventListener('mousedown', handleBlurClick);
    };
  }, [name, value, syncState, prevName, prevValue, prevSyncState, open]);

  return (
    <StyledAsyncFetchSelectClinic
      data-component-name="AsyncFetchSelectClinic"
      $partnerState={!!syncState}
      className={`asyncFetchSelect ${className}`}
      ref={componentRef}
    >
      <BasicTooltip text={name} placement="bottom-start">
        <input
          disabled={disabled}
          type="text"
          ref={inputRef}
          placeholder={placeholder}
          value={name}
          onChange={handleChangeInput}
          onClick={handleToggleDrop}
          onKeyDown={handleToggleOpen}
          className="AsyncFetchSelect__input"
        />
      </BasicTooltip>
      {fetchPartnerPending && (
        <span className="AsyncFetchSelect__loading">
          <CircularLoading size={13} color={color.blue} />
        </span>
      )}
      {!!syncState && (
        <span className="AsyncFetchSelect__partnerState" onClick={handleClickSyncState}>
          {syncState}
        </span>
      )}
      {name.length > 0 && (
        <button className="AsyncFetchSelect__cancel" onClick={handleCancel}>
          <ClearIcon fontSize="inherit" htmlColor={'#707070'} />
        </button>
      )}
      <button className="AsyncFetchSelect__drop" onClick={handleToggleDrop}>
        {!open ? (
          <ArrowDropDownIcon fontSize="inherit" htmlColor={'#707070'} />
        ) : (
          <ArrowDropUpIcon fontSize="inherit" htmlColor={'#707070'} />
        )}
      </button>
      {open && (
        <div
          className={cx('AsyncFetchSelect__dropdown_box', {
            isOpenUp: isOpenUp,
            isOpenDown: !isOpenUp,
          })}
        >
          <InfiniteScroll
            className="infinite-scroll-box"
            dataLength={convertPartnerList?.length}
            next={handleFetchSyncPartners}
            hasMore={hasMoreSyncPartners}
            loader={
              <div className="infinite-scroll-loading">
                <CircularLoading />
              </div>
            }
            scrollThreshold={0}
            height={'100px'}
          >
            {!!convertPartnerList?.length &&
              convertPartnerList.map((item: any, idx: number) => {
                if (exceptId && item[idKey] === exceptId) return;
                const partnerState = !!item?.isSentRequest
                  ? `${t('SYNC_PARTNER_STATE_REQ')}`
                  : !!item?.isSyncPartner
                  ? `${t('SYNC_PARTNER_STATE_ACCEPT')}`
                  : '';
                return (
                  <div
                    key={idx}
                    className={cx('AsyncFetchSelect__dropdown_menu_item', {
                      selected: value === item.userGroupIdx,
                      isPartnerState: !!partnerState,
                    })}
                    onClick={() => {
                      handleSelect({ ...item, partnerState });
                    }}
                  >
                    <CustomSpan>{item[labelKey]}</CustomSpan>
                    {!!partnerState && <span className="sent_user">{partnerState}</span>}
                    {item.accountType === 1 && (
                      <span className="sync_user">
                        <T>GLOBAL_SYNC</T>
                      </span>
                    )}
                  </div>
                );
              })}
          </InfiniteScroll>
        </div>
      )}
    </StyledAsyncFetchSelectClinic>
  );
};

const StyledAsyncFetchSelectClinic = styled.div<{
  $partnerState?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 100%;
  height: 40px;
  color: #222;
  font-size: 14px;
  text-align: initial;
  input,
  button {
    all: unset;
  }
  &.xs {
    height: 24px;
    font-size: 12px;
    .AsyncFetchSelect__dropdown_box {
      &.isOpenUp {
        bottom: 24px;
      }
      &.isOpenDown {
        top: 23px;
      }
    }
  }
  &.sm {
    height: 32px;
    font-size: 12px;
    .AsyncFetchSelect__dropdown_box {
      &.isOpenUp {
        bottom: 32px;
      }
      &.isOpenDown {
        top: 31px;
      }
    }
  }
  &.md {
    height: 40px;
    font-size: 14px;
    .AsyncFetchSelect__dropdown_box {
      &.isOpenUp {
        bottom: 40px;
      }
      &.isOpenDown {
        top: 39px;
      }
    }
  }
  &.lg {
    height: 46px;
    font-size: 16px;
    .AsyncFetchSelect__dropdown_box {
      &.isOpenUp {
        bottom: 46px;
      }
      &.isOpenDown {
        top: 45px;
      }
    }
  }
  &.xl {
    height: 56px;
    font-size: 16px;
    .AsyncFetchSelect__dropdown_box {
      &.isOpenUp {
        bottom: 56px;
      }
      &.isOpenDown {
        top: 55px;
      }
    }
  }
  .AsyncFetchSelect__input {
    width: 100%;
    height: 100%;
    padding: 0 8px;
    /* padding-right: 55px; */
    padding-right: ${({ $partnerState }) => (!!$partnerState ? '110px' : '55px')};

    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-color: #e1e1e1;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    &:hover {
      border-color: #7cbef8;
    }
    &:focus {
      border-color: ${color.blue};
    }
    &::placeholder {
      color: #e1e1e1;
    }
  }
  .AsyncFetchSelect__dropdown_box {
    z-index: 10;
    position: absolute;
    &.isOpenUp {
      bottom: 40px;
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.16);
    }
    &.isOpenDown {
      top: 39px;
      box-shadow: 0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%),
        0px 3px 14px 2px rgb(0 0 0 / 12%);
    }
    left: 0;
    width: 100%;
    /* padding: 5px 0; */
    max-height: 100px;
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;

    overflow-y: hidden;
    color: ${color.black_font};
    .AsyncFetchSelect__dropdown_menu_item {
      width: 100%;
      padding: 10px 8px;

      cursor: pointer;
      display: flex;
      justify-content: space-between;
      &:hover {
        background-color: #ebebeb;
      }
      &.selected {
        background-color: #f6f4f4;
      }
      &.isPartnerState {
        width: calc(100% -55px);
      }
    }
  }

  .AsyncFetchSelect__partnerState,
  .AsyncFetchSelect__drop,
  .AsyncFetchSelect__cancel,
  .AsyncFetchSelect__loading {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
  .AsyncFetchSelect__partnerState {
    right: 56px;
    font-size: 13px;
    font-weight: 400;
    color: #00a6e2;
  }
  .AsyncFetchSelect__loading {
    right: 58px;
  }
  .AsyncFetchSelect__cancel,
  .AsyncFetchSelect__drop {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    cursor: pointer;
    &:hover {
      background-color: rgb(245, 245, 245, 0.23);
    }
  }
  .AsyncFetchSelect__cancel {
    right: 29px;
    font-size: 18px;
  }
  .AsyncFetchSelect__drop {
    right: 6px;
    font-size: 23px;
  }
  .sent_user {
    font-size: 13px;
    font-weight: 400;
    color: #00a6e2;
    margin-left: 5px;
  }
  .sync_user {
    margin-left: 10px;
    background-color: #00a4e3;
    color: #fff;
    font-size: 11px;
    border-radius: 3px;
    letter-spacing: 0.11px;
    width: 45px;
    height: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export default React.memo(AsyncFetchSelectSyncPartner);
